import { getISOWeek, getISOWeekYear } from "date-fns";
import { FC, useContext } from "react";

import { CustomDayCellProps } from "..";
import { CheckOutlinedIcon } from "../../../../../assets/img/icons";
import Button from "../../../../../components/Button";
import Text from "../../../../../components/Text";

import { useMediaQuery } from "@mui/material";
import Stat from "../../../../../components/Text/Stat";
import Mindset from "../../../../../components/Mindset";
import { AuthContext } from "../../../../../contexts/AuthContext";
import { ValidatedWeek } from "../../../../../types/ValidatedWeek";
import {
    canShowWeekValidateInDisplayedMonth,
    isAtLeastThursdayOfISOWeek,
} from "../../../../../utils/date";
import cellStyles from "../styles.module.scss";
import styles from "./styles.module.scss";

type ValidatingCellProps = CustomDayCellProps & {
    handleValidWeekFormModalOpen: (
        weekNumber: number,
        year: number,
        weekResult?: string,
        breakdownByTimeslot?: { timeslot: string; total: number }[]
    ) => void;
    validatedWeeks: { [key: string]: ValidatedWeek }[];
    weeklyTotal?: number;
    weekBreakdownByTimeslot?: { timeslot: string; total: number }[];
    openRecapModal: (weekNumber: number, weekResult: string, year: number) => void;
};

const ValidatingCell: FC<ValidatingCellProps> = (props) => {
    const { getIsOwnDashboard, hasPermission } = useContext(AuthContext)!;
    const isMobile = useMediaQuery("(max-width: 1050px)");

    const {
        day,
        handleValidWeekFormModalOpen,
        validatedWeeks,
        weeklyTotal,
        weekBreakdownByTimeslot,
        hidePerformance,
        className,
        displayedMonth,
        onEmptyWeekdayClick: _omitEmptyWeekday,
        isEmptyCalendarDayAddAllowed: _omitEmptyPred,
        ...rest
    } = props;
    const isOutsideDay = className?.includes("rdp-outside");
    // Use ISO week/year to handle year crossover around late December
    const weekNumber = getISOWeek(day.date);
    const year = getISOWeekYear(day.date);

    // Transform validatedWeeks into a flat object with keys like "2025-W1"
    const validatedWeeksMap = (() => {
        if (!validatedWeeks) return {};

        // If it's an array, reduce it
        if (Array.isArray(validatedWeeks)) {
            return validatedWeeks.reduce((acc, weekObj) => {
                if (weekObj && typeof weekObj === "object") {
                    Object.entries(weekObj).forEach(([key, week]) => {
                        if (
                            week &&
                            typeof week === "object" &&
                            "year" in week &&
                            "weeknumber" in week
                        ) {
                            const weekKey = `${week.year}-W${week.weeknumber}`;
                            acc[weekKey] = week;
                        }
                    });
                }
                return acc;
            }, {} as { [key: string]: any });
        }

        // If it's already an object, process it directly
        if (typeof validatedWeeks === "object") {
            const result: { [key: string]: any } = {};
            Object.entries(validatedWeeks).forEach(([key, week]) => {
                if (
                    week &&
                    typeof week === "object" &&
                    "year" in week &&
                    "weeknumber" in week
                ) {
                    const weekKey = `${week.year}-W${week.weeknumber}`;
                    result[weekKey] = week;
                }
            });
            return result;
        }

        return {};
    })();

    const weekKey = `${year}-W${weekNumber}`;
    const validatedWeek = validatedWeeksMap[weekKey] as
        | ValidatedWeek
        | undefined;
    const isDayValidated = validatedWeek !== undefined;

    const canShowRecap =
        isDayValidated &&
        (weekNumber >= 51 || year > 2025) &&
        year >= 2025 &&
        ((getIsOwnDashboard() &&
            hasPermission("validated-week", "read")) ||
            hasPermission("admin-validated-weeks", "read"));

    // Check if week is in the future by comparing year and week number
    const currentDate = new Date();
    const currentWeek = getISOWeek(currentDate);
    const currentYear = getISOWeekYear(currentDate);
    const isFutureWeek =
        year > currentYear ||
        (year === currentYear && weekNumber > currentWeek);

    const canShowValidateButton =
        canShowWeekValidateInDisplayedMonth(day.date, displayedMonth) &&
        isAtLeastThursdayOfISOWeek(day.date);

    const weekTotalNum = Number(weeklyTotal || 0);
    const weekStatStraddleClass = isOutsideDay
        ? weekTotalNum > 0
            ? cellStyles.amountStraddlePaleProfit
            : weekTotalNum < 0
              ? cellStyles.amountStraddlePaleLoss
              : cellStyles.amountStraddlePaleFlat
        : "";

    return (
        <div
            {...rest}
            className={`${styles.validatingCell} ${
                isOutsideDay ? styles.validatingCellOutside : ""
            } ${className || ""}`}
        >
            <div className={styles.validatingCellContent}>
                <Text variant="body-bold" className={styles.weekNumber}>
                    <span>{`S`}</span>
                    <span className={styles.weekLabel}>{`emaine `}</span>
                    {`${weekNumber}`}
                </Text>
                <div className={styles.weekAmount}>
                    {!hidePerformance && (
                        <Stat
                            type="currency"
                            variant="body-bold"
                            className={weekStatStraddleClass}
                        >
                            {weeklyTotal || 0}
                        </Stat>
                    )}
                    {canShowRecap && validatedWeek?.mindset && (
                        <>
                            {!hidePerformance && " - "}
                            <Mindset mindset={validatedWeek.mindset} />
                        </>
                    )}
                </div>
                {!isFutureWeek &&
                    (isDayValidated ? (
                        canShowRecap ? (
                            <Button
                                className={styles.recapButton}
                                size="sx"
                                variant="transparent"
                                onClick={() =>
                                    props.openRecapModal?.(
                                        weekNumber,
                                        String(weeklyTotal || 0),
                                        year
                                    )
                                }
                            >
                                {isMobile ? "Récap." : "Récapitulatif"}
                            </Button>
                        ) : (
                            <CheckOutlinedIcon
                                className={styles.validatedIcon}
                            />
                        )
                    ) : (
                        getIsOwnDashboard() &&
                        props.weekHasAnySession !== false &&
                        canShowValidateButton && (
                            <Button
                                className={styles.validateButton}
                                label={isMobile ? "" : "Valider"}
                                size="sx"
                                Icon={CheckOutlinedIcon}
                                onClick={() =>
                                    handleValidWeekFormModalOpen(
                                        weekNumber,
                                        year,
                                        String(weeklyTotal ?? 0),
                                        weekBreakdownByTimeslot
                                    )
                                }
                            />
                        )
                    ))}
            </div>
        </div>
    );
};

export default ValidatingCell;
